import { dbService, firebaseInstance } from '../firebaseInstance';
import { saveDraft } from './draftManager';

/**
 * 아이디어 목록 조회
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} 아이디어 목록
 */
export const getIdeations = async (userId) => {
  try {
    console.log('[getIdeations] Start:', { userId });

    if (!userId) {
      throw new Error('사용자 ID가 필요합니다.');
    }

    // 복합 인덱스가 없을 경우를 대비하여 클라이언트 측 필터링 사용
    const snapshot = await dbService
      .collection('user')
      .doc(userId)
      .collection('ideations')
      .get();

    const ideations = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(ideation => ideation.status === 'active')
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    console.log('[getIdeations] ✅ Loaded:', ideations.length, 'active ideations');
    return ideations;
  } catch (error) {
    console.error('[getIdeations] ❌ Failed:', error);
    throw new Error(`아이디어 목록 조회 실패: ${error.message}`);
  }
};

/**
 * JSON 파일에서 아이디어 일괄 업로드
 * @param {string} userId - 사용자 ID
 * @param {Array} jsonData - [{text: string}, ...] 형식의 배열
 * @returns {Promise<number>} 업로드된 아이디어 개수
 */
export const uploadIdeationsFromJSON = async (userId, jsonData) => {
  try {
    console.log('[uploadIdeationsFromJSON] Start:', { userId, count: jsonData.length });

    if (!userId) {
      throw new Error('사용자 ID가 필요합니다.');
    }

    if (!Array.isArray(jsonData) || jsonData.length === 0) {
      throw new Error('유효한 JSON 배열이 필요합니다.');
    }

    // 현재 최대 order 값 조회
    const existingSnapshot = await dbService
      .collection('user')
      .doc(userId)
      .collection('ideations')
      .orderBy('order', 'desc')
      .limit(1)
      .get();

    let maxOrder = 0;
    if (!existingSnapshot.empty) {
      maxOrder = existingSnapshot.docs[0].data().order || 0;
    }

    // Batch 작업으로 한번에 업로드
    const batch = dbService.batch();
    const ideationsRef = dbService
      .collection('user')
      .doc(userId)
      .collection('ideations');

    let uploadedCount = 0;
    jsonData.forEach((item, index) => {
      if (!item || typeof item.text !== 'string' || item.text.trim().length === 0) {
        console.warn('[uploadIdeationsFromJSON] Skipping invalid item:', item);
        return;
      }

      const newDocRef = ideationsRef.doc();
      batch.set(newDocRef, {
        text: item.text.trim(),
        order: maxOrder + index + 1,
        createdAt: firebaseInstance.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebaseInstance.firestore.FieldValue.serverTimestamp(),
        status: 'active',
        exportedToDraftId: null,
        exportedAt: null
      });
      uploadedCount++;
    });

    await batch.commit();

    console.log('[uploadIdeationsFromJSON] ✅ Uploaded:', uploadedCount, 'ideations');
    return uploadedCount;
  } catch (error) {
    console.error('[uploadIdeationsFromJSON] ❌ Failed:', error);
    throw new Error(`아이디어 업로드 실패: ${error.message}`);
  }
};

/**
 * 아이디어 단일 조회
 * @param {string} userId - 사용자 ID
 * @param {string} ideationId - 아이디어 ID
 * @returns {Promise<Object>} 아이디어 데이터
 */
export const getIdeation = async (userId, ideationId) => {
  try {
    console.log('[getIdeation] Start:', { userId, ideationId });

    if (!userId || !ideationId) {
      throw new Error('사용자 ID와 아이디어 ID가 필요합니다.');
    }

    const doc = await dbService
      .collection('user')
      .doc(userId)
      .collection('ideations')
      .doc(ideationId)
      .get();

    if (!doc.exists) {
      throw new Error('아이디어를 찾을 수 없습니다.');
    }

    const ideation = {
      id: doc.id,
      ...doc.data()
    };

    console.log('[getIdeation] ✅ Loaded:', ideation);
    return ideation;
  } catch (error) {
    console.error('[getIdeation] ❌ Failed:', error);
    throw new Error(`아이디어 조회 실패: ${error.message}`);
  }
};

/**
 * 아이디어 수정
 * @param {string} userId - 사용자 ID
 * @param {string} ideationId - 아이디어 ID
 * @param {string} text - 수정할 텍스트
 * @returns {Promise<void>}
 */
export const updateIdeation = async (userId, ideationId, text) => {
  try {
    console.log('[updateIdeation] Start:', { userId, ideationId, textLength: text.length });

    if (!userId || !ideationId) {
      throw new Error('사용자 ID와 아이디어 ID가 필요합니다.');
    }

    if (!text || text.trim().length === 0) {
      throw new Error('내용을 입력해주세요.');
    }

    await dbService
      .collection('user')
      .doc(userId)
      .collection('ideations')
      .doc(ideationId)
      .update({
        text: text.trim(),
        updatedAt: firebaseInstance.firestore.FieldValue.serverTimestamp()
      });

    console.log('[updateIdeation] ✅ Updated:', ideationId);
  } catch (error) {
    console.error('[updateIdeation] ❌ Failed:', error);
    throw new Error(`아이디어 수정 실패: ${error.message}`);
  }
};

/**
 * 아이디어 삭제
 * @param {string} userId - 사용자 ID
 * @param {string} ideationId - 아이디어 ID
 * @returns {Promise<void>}
 */
export const deleteIdeation = async (userId, ideationId) => {
  try {
    console.log('[deleteIdeation] Start:', { userId, ideationId });

    if (!userId || !ideationId) {
      throw new Error('사용자 ID와 아이디어 ID가 필요합니다.');
    }

    await dbService
      .collection('user')
      .doc(userId)
      .collection('ideations')
      .doc(ideationId)
      .delete();

    console.log('[deleteIdeation] ✅ Deleted:', ideationId);
  } catch (error) {
    console.error('[deleteIdeation] ❌ Failed:', error);
    throw new Error(`아이디어 삭제 실패: ${error.message}`);
  }
};

/**
 * 아이디어를 임시글로 내보내기
 * saveDraft를 사용하여 draft 생성 후, ideation 상태를 'exported'로 변경
 *
 * @param {string} userId - 사용자 ID
 * @param {string} ideationId - 아이디어 ID
 * @param {string} ideationText - 아이디어 텍스트
 * @returns {Promise<string>} 생성된 draft ID
 */
export const exportIdeationToDraft = async (userId, ideationId, ideationText) => {
  try {
    console.log('[exportIdeationToDraft] Start:', { userId, ideationId, textLength: ideationText?.length });

    if (!userId || !ideationId) {
      throw new Error('사용자 ID와 아이디어 ID가 필요합니다.');
    }

    if (!ideationText || ideationText.trim().length === 0) {
      throw new Error('내용이 비어있습니다.');
    }

    // 1. saveDraft 함수를 사용하여 draft 생성
    // Draft 구조에 맞게 데이터 변환
    const draftData = {
      threads: [
        {
          id: 1,
          text: ideationText.trim(),
          isMain: true
        }
      ],
      mediaFiles: [],
      replyMediaFiles: {},
      collectionConfig: {
        mode: 'none',
        autoAddToCollection: false
      },
      scheduleDateTime: new Date() // 현재 시간 + 3분으로 초기화
    };

    console.log('[exportIdeationToDraft] Creating draft with data:', draftData);

    // saveDraft 호출 - 새로운 draft 생성 (draftId null)
    const result = await saveDraft(userId, draftData, null);

    if (!result.success || !result.draftId) {
      throw new Error('Draft 생성에 실패했습니다.');
    }

    const draftId = result.draftId;
    console.log('[exportIdeationToDraft] Draft created:', draftId);

    // 2. ideation 문서 상태를 'exported'로 업데이트
    await dbService
      .collection('user')
      .doc(userId)
      .collection('ideations')
      .doc(ideationId)
      .update({
        status: 'exported',
        exportedToDraftId: draftId,
        exportedAt: firebaseInstance.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebaseInstance.firestore.FieldValue.serverTimestamp()
      });

    console.log('[exportIdeationToDraft] ✅ Ideation exported to draft:', { ideationId, draftId });
    return draftId;
  } catch (error) {
    console.error('[exportIdeationToDraft] ❌ Failed:', error);
    throw new Error(`임시글로 내보내기 실패: ${error.message}`);
  }
};

/**
 * 내보낸 아이디어 포함하여 모든 아이디어 조회 (관리자용)
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} 모든 아이디어 목록
 */
export const getAllIdeations = async (userId) => {
  try {
    console.log('[getAllIdeations] Start:', { userId });

    if (!userId) {
      throw new Error('사용자 ID가 필요합니다.');
    }

    const snapshot = await dbService
      .collection('user')
      .doc(userId)
      .collection('ideations')
      .orderBy('order', 'asc')
      .get();

    const ideations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    console.log('[getAllIdeations] ✅ Loaded:', ideations.length, 'ideations (including exported)');
    return ideations;
  } catch (error) {
    console.error('[getAllIdeations] ❌ Failed:', error);
    throw new Error(`모든 아이디어 조회 실패: ${error.message}`);
  }
};
